import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Save, ExternalLink } from "lucide-react";
import { useCrm } from "../../pages/crm/CrmApp";
import { toast } from "../../hooks/use-toast";
import { Card, ImageUpload, inputCls, Labeled } from "../market/admin/ui";

const WaSettings = () => {
  const { api, me, reloadMe } = useCrm();
  const wa = me.org.whatsapp || {};
  const [f, setF] = useState({ access_token: "", phone_number_id: wa.phone_number_id || "", template_name: wa.template_name || "", template_lang: wa.template_lang || "es_MX" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await api("/settings/whatsapp", { method: "PUT", body: f });
      setF((x) => ({ ...x, access_token: "" }));
      await reloadMe();
      toast({ title: "WhatsApp conectado" });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("¿Desconectar WhatsApp automático?")) return;
    await api("/settings/whatsapp", { method: "DELETE" });
    reloadMe();
  };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Card title="WhatsApp automático (número de empresa)">
      <p className="text-sm text-slate-600">Los mensajes y cotizaciones salen desde el número de WhatsApp Business de esta empresa (API oficial de Meta). Si no está conectado, se abre WhatsApp con el mensaje listo.</p>
      {me.org.whatsapp_ready ? (
        <div className="flex items-center justify-between rounded-xl bg-emerald-50 p-3" data-testid="crm-wa-connected">
          <span className="flex items-center gap-2 text-sm font-medium text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Conectado · token {wa.hint}</span>
          <button onClick={remove} className="text-xs text-red-600 underline" data-testid="crm-wa-disconnect">Desconectar</button>
        </div>
      ) : (
        <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="crm-wa-not-connected">No conectado.</p>
      )}
      <Labeled label="Token de acceso permanente" hint="Meta Business → Usuarios del sistema → Generar token con permiso whatsapp_business_messaging. Se guarda cifrado.">
        <input type="password" autoComplete="off" className={inputCls} value={f.access_token} onChange={set("access_token")} placeholder={me.org.whatsapp_ready ? "Pega uno nuevo para cambiarlo" : "EAAG…"} data-testid="crm-wa-token" />
      </Labeled>
      <Labeled label="ID del número de teléfono" hint="WhatsApp → Configuración de la API → Phone number ID (no es el número).">
        <input className={inputCls} value={f.phone_number_id} onChange={set("phone_number_id")} data-testid="crm-wa-phone-id" />
      </Labeled>
      <div className="grid grid-cols-[1fr_100px] gap-2">
        <Labeled label="Plantilla aprobada (opcional)" hint="Se usa cuando el cliente no te ha escrito en 24 h. Debe tener 1 variable: el nombre.">
          <input className={inputCls} value={f.template_name} onChange={set("template_name")} placeholder="seguimiento_cliente" data-testid="crm-wa-template" />
        </Labeled>
        <Labeled label="Idioma"><input className={inputCls} value={f.template_lang} onChange={set("template_lang")} data-testid="crm-wa-lang" /></Labeled>
      </div>
      {err && <p className="text-sm text-red-600" data-testid="crm-wa-error">{err}</p>}
      <button disabled={busy || !f.access_token || !f.phone_number_id} onClick={save} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="crm-wa-save">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Conectar WhatsApp
      </button>
      <a href="https://business.facebook.com/latest/whatsapp_manager" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600">Abrir WhatsApp Manager <ExternalLink className="h-3 w-3" /></a>
    </Card>
  );
};

export const CrmSettings = () => {
  const { api, me, reloadMe } = useCrm();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF({ name: me.org.name, domain: me.org.domain, theme: { ...me.org.theme }, quote: { ...me.org.quote }, company: { ...me.org.company } });
  }, [me]);
  if (!f) return null;
  const sub = (g, k) => (e) => setF((x) => ({ ...x, [g]: { ...x[g], [k]: e.target ? e.target.value : e } }));
  const save = async () => {
    setBusy(true);
    try {
      await api("/settings", { method: "PUT", body: { ...f, quote: { ...f.quote, tax_pct: Number(f.quote.tax_pct) || 0, valid_days: Number(f.quote.valid_days) || 15 } } });
      await reloadMe();
      toast({ title: "Ajustes guardados" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">Ajustes</h1>
      <Card title="Empresa y marca">
        <Labeled label="Nombre de la empresa"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="crm-set-name" /></Labeled>
        <Labeled label="Logo (sale en el PDF de cotización)"><ImageUpload value={f.theme.logo} onChange={sub("theme", "logo")} label="Subir logo" testid="crm-set-logo" /></Labeled>
        <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-2.5">
          <input type="color" value={f.theme.primary || "#2563EB"} onChange={sub("theme", "primary")} className="h-8 w-8 cursor-pointer rounded border-0 bg-transparent" data-testid="crm-set-color" />
          <span className="text-sm text-slate-600">Color principal</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Teléfono"><input className={inputCls} value={f.company.phone || ""} onChange={sub("company", "phone")} data-testid="crm-set-phone" /></Labeled>
          <Labeled label="Correo"><input className={inputCls} value={f.company.email || ""} onChange={sub("company", "email")} data-testid="crm-set-email" /></Labeled>
          <Labeled label="RFC"><input className={inputCls} value={f.company.rfc || ""} onChange={sub("company", "rfc")} data-testid="crm-set-rfc" /></Labeled>
          <Labeled label="Dominio propio"><input className={inputCls} value={f.domain || ""} onChange={(e) => setF({ ...f, domain: e.target.value })} placeholder="crm.miempresa.com" data-testid="crm-set-domain" /></Labeled>
        </div>
        <Labeled label="Dirección"><input className={inputCls} value={f.company.address || ""} onChange={sub("company", "address")} data-testid="crm-set-address" /></Labeled>
      </Card>
      <Card title="Cotizaciones">
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="IVA por defecto %"><input type="number" className={inputCls} value={f.quote.tax_pct} onChange={sub("quote", "tax_pct")} data-testid="crm-set-tax" /></Labeled>
          <Labeled label="Vigencia (días)"><input type="number" className={inputCls} value={f.quote.valid_days} onChange={sub("quote", "valid_days")} data-testid="crm-set-valid" /></Labeled>
        </div>
        <Labeled label="Términos y condiciones"><textarea rows={4} className={inputCls} value={f.quote.terms || ""} onChange={sub("quote", "terms")} data-testid="crm-set-terms" /></Labeled>
      </Card>
      <button onClick={save} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white" style={{ background: "var(--crm)" }} data-testid="crm-settings-save">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar ajustes
      </button>
      <WaSettings />
    </div>
  );
};
